// components/Equipment/JobCards/ViewJobCards.js

import React, { useState, useEffect, useContext } from 'react';
import { Container, Table, Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { firestore } from '../../utils/firebase';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../Admin/UserContext';

const ViewJobCards = () => {
    const [jobCards, setJobCards] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const userData = useContext(UserContext);
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        fetchData();
    }, [selectedEmployee, statusFilter]);   // ✅ now it watches both filters


    const fetchData = async () => {
        setLoading(true);
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const userSnapshot = await firestore.collection('users').doc(currentUser.uid).get();
        const company = userSnapshot.data().company;

        // Employees
        const usersSnapshot = await firestore.collection('users').where('company', '==', company).get();
        const employeeList = usersSnapshot.docs.map(doc => ({
            id: doc.id,
            name: `${doc.data().name} ${doc.data().surname}`
        }));
        setEmployees(employeeList);

        // ✅ Pull records from maintenanceLogs
        let maintenanceRef = firestore.collection('companies').doc(company).collection('maintenanceLogs');

        if (selectedEmployee) {
            maintenanceRef = maintenanceRef.where('assignedEmployeeId', '==', selectedEmployee);
        }

        const maintenanceSnapshot = await maintenanceRef.get();
        const allRecords = maintenanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // ✅ Filter for jobcard logic
        let filteredCards = allRecords.filter(card =>
            (card.scheduled === false) ||
            (card.scheduled === true && card.ScheduledApproved === true)
        );

        // ✅ Apply Open/Closed filter
        if (statusFilter) {
            filteredCards = filteredCards.filter(card => {
                const status = card.status || (card.JobcardOpen ? 'Open' : 'Closed');
                return status === statusFilter;
            });
        }

        // ✅ Map maintenance record fields to JobCard table fields
        const jobCardsData = filteredCards.map(record => ({
            jobCardNumber: record.linkedJobCardNumber || record.id,
            equipmentNumber: record.equipmentNumber,
            plannedDate: record.date,
            priority: record.priority || 'Medium',
            status: record.status || (record.JobcardOpen ? 'Open' : 'Closed'),
            assignedEmployeeId: record.assignedEmployeeId || '',
            assignedEmployeeName: record.employee || '',
            id: record.id
        }));

        // ✅ Sort job cards by jobCardNumber
        jobCardsData.sort((a, b) => {
            const numA = parseInt(a.jobCardNumber.replace('MB-JC-', ''), 10);
            const numB = parseInt(b.jobCardNumber.replace('MB-JC-', ''), 10);
            return numA - numB;
        });

        setJobCards(jobCardsData);

        setLoading(false);
    };

    const handleDelete = async (jobCardNumber) => {
        if (window.confirm("Are you sure you want to delete this job card?")) {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            const userSnapshot = await firestore.collection('users').doc(currentUser.uid).get();
            const company = userSnapshot.data().company;

            await firestore.collection('companies').doc(company)
                .collection('JobCards').doc(jobCardNumber).delete();

            setJobCards(prev => prev.filter(card => card.jobCardNumber !== jobCardNumber));
        }
    };

    if (loading) {
        return (
            <div className="loading-spinner-container">
                <Spinner animation="border" role="status" variant="success" className="loading-spinner">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
            <div style={{ width: '100%' }}>
                <h1 className="mb-4">View Job Cards</h1>

                <Row className="mb-4">
                    <Col md={6}>
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Select className="mb-4" value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
                                    <option value="">Filter by Employee</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>

                            {/* ✅ New: Status filter */}
                            <Col md={6}>
                                <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="">Show All</option>
                                    <option value="Open">Open</option>
                                    <option value="Closed">Closed</option>
                                </Form.Select>
                            </Col>
                        </Row>

                    </Col>
                    <Col md={6}>
                        <Button variant="outline-success" onClick={() => navigate('/equipment-maintenance')}>
                            Back to Maintenance
                        </Button>
                    </Col>
                </Row>

                <div style={{ overflowX: 'auto' }}>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Job Card No.</th>
                                <th>Equipment No.</th>
                                <th>Planned Date</th>         {/* ✅ New column */}
                                <th className="d-none d-md-table-cell">Priority</th>
                                <th>Status</th>
                                <th className="d-none d-md-table-cell">Assigned To</th>
                                <th>View</th>
                                <th>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobCards.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center">No job cards found.</td>
                                </tr>
                            ) : (
                                jobCards.map(card => (
                                    <tr key={card.jobCardNumber}>
                                        <td>{card.jobCardNumber}</td>
                                        <td>{card.equipmentNumber}</td>
                                        <td>
                                            {card.plannedDate ?
                                                new Date(card.plannedDate.seconds * 1000).toLocaleDateString() : 'N/A'}
                                        </td>  {/* ✅ Display planned date */}
                                        <td className="d-none d-md-table-cell">{card.priority}</td>     
                                        <td>{card.status}</td>
                                        <td className="d-none d-md-table-cell">{card.assignedEmployeeName}</td>
                                        <td>
                                            <Button
                                                variant="info"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => navigate(`/job-card/${card.jobCardNumber}`)}
                                            >
                                                View
                                            </Button>
                                            
                                        </td>
                                        <td>
                                           
                                            {userData?.role === 'Admin' && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(card.jobCardNumber)}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </Container>
    );
};

export default ViewJobCards;
